import { MapPin, Briefcase, Crown, CheckCircle, Users } from 'lucide-react';
import { canCreateGig } from '../utils/accessPolicy';

interface GigCardProps {
  gig: any;
  hasInterest: boolean;
  onViewDetails: (gigId: string) => void;
  onShowInterest: (gigId: string) => void;
  onViewProfile: (userId: string) => void;
  /** When true (own gig), show "Interesserte" instead of "Vis interesse" */
  isOwnGig?: boolean;
  /** Callback to open interests view for this gig (used when isOwnGig) */
  onViewInterests?: (gigId: string, gigTitle: string) => void;
}

export function GigCard({ gig, hasInterest, onViewDetails, onShowInterest, onViewProfile, isOwnGig, onViewInterests }: GigCardProps) {
  const gigImageUrl = gig?.imageUrl || gig?.image_url || gig?.image;

  const handleCardClick = () => {
    onViewDetails(gig.id);
  };

  const handlePosterClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewProfile(gig.poster.id);
  };

  const handleInterestClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShowInterest(gig.id);
  };

  const handleViewInterestsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewInterests?.(gig.id, gig.title || '');
  };

  return (
    <div 
      className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.995] border-2 border-purple-100 cursor-pointer transform-gpu"
      onClick={handleCardClick}
    >
      <div className="p-5">
        {/* Poster info */}
        <div 
          className="flex items-center gap-3 mb-4 cursor-pointer hover:bg-purple-50 -m-2 p-2 rounded-lg transition-colors duration-150"
          onClick={handlePosterClick}
        >
          <div className="relative">
            {gig.poster.profileImage ? (
              <img
                src={gig.poster.profileImage}
                alt={gig.poster.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
            )}
            {/* Arbeidsgiver Pluss badge */}
            {canCreateGig(gig.poster) && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center border-2 border-white">
                <Crown className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-purple-900 truncate">{gig.poster.name}</h4>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              {canCreateGig(gig.poster) ? (
                <>
                  <Crown className="w-3 h-3 text-orange-500" />
                  Arbeidsgiver Pluss
                </>
              ) : (
                <>
                  <Briefcase className="w-3 h-3 text-gray-500" />
                  Arbeidsgiver
                </>
              )}
            </p>
          </div>
        </div>

        {/* Title */}
        <h3 className="mb-2 text-purple-900">{gig.title}</h3>

        {/* Gig image */}
        {gigImageUrl && (
          <div className="mb-4 overflow-hidden rounded-xl border border-purple-100">
            <img
              src={gigImageUrl}
              alt={gig.title}
              className="h-44 w-full object-cover"
              loading="lazy"
            />
          </div>
        )}

        {/* Description (truncated) */}
        <p className="text-gray-600 mb-4 line-clamp-2">{gig.description}</p>

        {/* Skills */}
        {gig.skills && gig.skills.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {gig.skills.slice(0, 3).map((skill: string, index: number) => (
              <span
                key={index}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
              >
                {skill}
              </span>
            ))}
            {gig.skills.length > 3 && (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                +{gig.skills.length - 3} til
              </span>
            )}
          </div>
        )}

        {/* Meta info */}
        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
          {gig.county && (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{gig.county}</span>
            </div>
          )}
          {gig.budget && (
            <div className="flex items-center gap-1">
              <Briefcase className="w-4 h-4" />
              <span>{gig.budget}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleCardClick}
            className="flex-1 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-all duration-150 active:scale-[0.98] font-semibold"
          >
            Se detaljer
          </button>
          {isOwnGig && onViewInterests ? (
            <button
              onClick={handleViewInterestsClick}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-150 active:scale-[0.98] font-semibold flex items-center justify-center gap-2"
            >
              <Users className="w-4 h-4" />
              Interesserte
            </button>
          ) : hasInterest ? (
            <button
              disabled
              className="flex-1 px-4 py-2 bg-green-100 text-green-700 rounded-lg cursor-not-allowed font-semibold flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Interesse sendt
            </button>
          ) : (
            <button
              onClick={handleInterestClick}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all duration-150 active:scale-[0.98] font-semibold"
            >
              Vis interesse
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
